import React, { useState } from 'react'

import { Button, Divider, Typography } from '@mui/material'
import { Box } from '@mui/system'
import { GAME_TYPE } from 'constants/enums/GameTypeEnum'
import { ToastSeverity } from 'constants/enums/ToastSeverityEnum'
import { brainstormText } from 'content/brainstormText'

import { RoundRobinResponseResponse } from 'models/ideaModels'

import { useToastContext } from 'contexts/ToastProvider'

import ButtonWithIconComponent from 'components/ButtonWithIcon'
import GeneratedExampleDropdown from 'components/GeneratedExampleDropdown'
import LoadingSpinner from 'components/LoadingSpinner/LoadingSpinner'
import Section2Title from 'components/Section2Title'
import UserEducationModal from 'components/UserEducationModal'

import editIcon from 'assets/images/Pencil.svg'

import { commonStyles } from 'styles/common.styles'

import PromptCarouselComponent from '../Carousel'
import { styles } from './Section2.styles'

export const TEST_ID = 'round-robin-section2'

export type Props = {
	changePrompts: (newValues: string[]) => void
	createPrompt: () => void
	currentPrompts: string[]
	currentResponse: string
	loading: boolean
	roundRobinPrompt?: RoundRobinResponseResponse
}

const buildResponseItem = (solution: string) => {
	const stringSol = solution.split('\n')
	return (
		<Box sx={styles.exampleIdeasBox}>
			<ol data-testid="gen-ai-response-list">
				{stringSol.map((el: string, i: number) => (
					<Typography
						fontSize={16}
						component="li"
						key={`ai-solution-${i}`}
						variant="body1"
						style={styles.listItem}
					>
						{el}
					</Typography>
				))}
			</ol>
		</Box>
	)
}

const Section2 = (props: Props) => {
	const { showAlert } = useToastContext()
	const [currentIndex, setCurrentIndex] = useState<number>(0)
	const [editPromptsMode, setEditPromptsMode] = useState(false)
	const [temporalPromptsEditMode, setTemporalPrompts] = useState<string[]>([])
	const [isGenAiLoading, setIsGenAiLoading] = useState(false)
	const [showAIExampleIdea, setShowAIExampleIdea] = useState(false)

	const onClickEdit = () => {
		setTemporalPrompts(props.currentPrompts)
		setEditPromptsMode(true)
	}

	const onClickCancelEdit = () => {
		setEditPromptsMode(false)
	}

	const onClickSaveEdit = async () => {
		const lastEditedIdea = document.getElementsByTagName('textarea')
		if (
			props.roundRobinPrompt &&
			lastEditedIdea &&
			lastEditedIdea.length >= 1
		) {
			if (
				props.roundRobinPrompt.data.ideas[currentIndex] !==
				lastEditedIdea[0].value
			) {
				props.changePrompts(temporalPromptsEditMode)
				try {
					setEditPromptsMode(false)
					if (showAlert) {
						showAlert({
							objectType: 'editPrompt',
							severity: ToastSeverity.SUCCESS,
						})
					}
				} catch (error) {
					if (showAlert) {
						showAlert({
							objectType: 'defaultError',
							severity: ToastSeverity.ERROR,
						})
					}
				}
			}
		}
	}

	const setPromptValue = (value: string) => {
		const newTemporalPrompts = [...temporalPromptsEditMode]
		newTemporalPrompts[currentIndex] = value
		setTemporalPrompts(newTemporalPrompts)
	}

	return (
		<>
			<Section2Title />
			<UserEducationModal />
			<Box display={'flex'}>
				<Box marginRight={'2rem'} sx={styles.sideBar} />
				<Box
					display="flex"
					flexDirection="column"
					gap="2rem"
					flexGrow="1"
					paddingY={{ xs: '0', sm: '1rem' }}
				>
					<Box
						display={'flex'}
						flexDirection={{
							xs: 'column',
							sm: 'row',
						}}
						justifyContent={'space-between'}
						alignItems={{
							xs: 'flex-start',
							sm: 'center',
						}}
						gap={'1rem'}
					>
						<Typography sx={commonStyles.header}>
							Review the following sections as conversation and
							research starters to refine and iterate on your
							initial idea.
						</Typography>
					</Box>
					<Box sx={{ marginX: { xs: '0.5rem', md: '2rem' } }}>
						{props.loading ? (
							<LoadingSpinner />
						) : (
							<>
								<PromptCarouselComponent
									setCurrentIndex={setCurrentIndex}
									currentIndex={currentIndex}
									currentPrompts={props.currentPrompts}
									promptsEditMode={temporalPromptsEditMode}
									editable={editPromptsMode}
									setPromptValue={setPromptValue}
									disabledArrows={editPromptsMode}
								/>
								<Box sx={styles.CarouselName}>
									<Box
										flexDirection={{
											xs: 'column',
											sm: 'row',
										}}
										sx={styles.PromptsCarouselName}
									>
										<Button
											data-testid="Industry Context"
											sx={{
												...styles.PromptsName,

												borderTopLeftRadius: {
													sm: '18px',
													md: '18px',
												},
												borderBottomLeftRadius: {
													sm: '18px',
													md: '18px',
												},
											}}
											style={{
												backgroundColor:
													currentIndex === 0
														? 'rgba(255, 190, 207, 1)'
														: 'white',
											}}
											onClick={() => setCurrentIndex(0)}
											disabled={editPromptsMode}
										>
											Industry Context
										</Button>
										<Divider
											orientation="vertical"
											variant="middle"
											flexItem
											sx={styles.divider}
										/>
										<Button
											data-testid="Potential Challenges"
											sx={styles.PromptsName}
											style={{
												backgroundColor:
													currentIndex === 1
														? 'rgba(255, 190, 207, 1)'
														: 'white',
											}}
											onClick={() => setCurrentIndex(1)}
											disabled={editPromptsMode}
										>
											Potential Challenges
										</Button>
										<Divider
											orientation="vertical"
											variant="middle"
											flexItem
											sx={styles.divider}
										/>
										<Button
											data-testid="Testing Ideas"
											sx={styles.PromptsName}
											style={{
												backgroundColor:
													currentIndex === 2
														? 'rgba(255, 190, 207, 1)'
														: 'white',
											}}
											onClick={() => setCurrentIndex(2)}
											disabled={editPromptsMode}
										>
											Testing Ideas
										</Button>
										<Divider
											orientation="vertical"
											variant="middle"
											flexItem
											sx={styles.divider}
										/>
										<Button
											data-testid="Scale Opportunities"
											sx={{
												...styles.PromptsName,

												borderTopRightRadius: {
													sm: '18px',
													md: '18px',
												},
												borderBottomRightRadius: {
													sm: '18px',
													md: '18px',
												},
											}}
											style={{
												backgroundColor:
													currentIndex === 3
														? 'rgba(255, 190, 207, 1)'
														: 'white',
											}}
											onClick={() => setCurrentIndex(3)}
											disabled={editPromptsMode}
										>
											Scale Opportunities
										</Button>
									</Box>
								</Box>
							</>
						)}
					</Box>
					{editPromptsMode ? (
						<Box
							display="flex"
							flexDirection={{
								xs: 'column',
								sm: 'row',
							}}
							gap="2rem"
						>
							<Button
								color="secondary"
								variant="contained"
								data-testid={`${TEST_ID}__edit-save-button`}
								onClick={async () => {
									setIsGenAiLoading(true)
									await onClickSaveEdit()
									setIsGenAiLoading(false)
								}}
							>
								<Typography>SAVE CHANGES</Typography>
							</Button>
							<Button
								variant="text"
								color="secondary"
								data-testid={`${TEST_ID}__edit-cancel-button`}
								onClick={onClickCancelEdit}
							>
								<Typography>CANCEL</Typography>
							</Button>
						</Box>
					) : (
						<Box
							display={'flex'}
							alignItems={'center'}
							justifyContent={'end'}
						>
							<ButtonWithIconComponent
								sx={{ padding: 0 }}
								disabled={props.loading}
								iconSvg={editIcon}
								text="EDIT"
								data-testid={`${TEST_ID}__edit-button`}
								onClickAction={onClickEdit}
							/>
						</Box>
					)}

					<Typography sx={commonStyles.header}>
						{brainstormText.global.ideasDropdown}
					</Typography>
					<GeneratedExampleDropdown
						gameTypeEnum={GAME_TYPE.ROUND_ROBIN}
						headerText={''}
						isLoading={isGenAiLoading}
						customResponse={buildResponseItem(
							props.currentResponse
						)}
						showAIExampleIdea={showAIExampleIdea}
						setShowAIExampleIdea={setShowAIExampleIdea}
					/>
				</Box>
			</Box>
		</>
	)
}
export default Section2
